import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useBooking, scrollToId } from "@/booking";
import { IMAGES } from "@/data/content";

const LINES = ["The Gap Between", "Who You Are & Who", "You're Capable Of", "Becoming"];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.13, delayChildren: 0.3 } },
};
const lineVariant = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const openBooking = useBooking();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="top" data-testid="hero-section" ref={ref} className="relative overflow-hidden pb-24 pt-32 sm:pt-36 lg:pt-40">
      <motion.div aria-hidden style={{ y: glowY }} className="pointer-events-none absolute -right-40 -top-40 h-[36rem] w-[36rem] rounded-full bg-peach/25 blur-[130px]" />
      <div aria-hidden className="pointer-events-none absolute -left-52 top-1/3 h-[28rem] w-[28rem] rounded-full bg-rosegold/20 blur-[120px]" />
      <span aria-hidden className="text-outline pointer-events-none absolute -bottom-8 left-0 select-none whitespace-nowrap font-display text-[22vw] leading-none opacity-[0.07]">
        BECOME
      </span>

      <div className="mx-auto grid max-w-6xl items-center gap-14 px-5 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-peach/35 bg-cream-dark px-4 py-2"
          >
            <span className="h-2 w-2 rounded-full bg-peach-dark" />
            <span className="font-heading text-[11px] font-bold uppercase tracking-[0.24em] text-peach-dark">
              Transformative Mentor &amp; Guide
            </span>
          </motion.div>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="font-display uppercase leading-[0.94] tracking-wide text-ink"
          >
            {LINES.map((l, i) => (
              <span key={l} className="block overflow-hidden pb-1">
                <motion.span variants={lineVariant} className="block text-[11.5vw] sm:text-6xl lg:text-7xl">
                  {l}
                </motion.span>
              </span>
            ))}
            <span className="block overflow-hidden pb-2">
              <motion.span
                variants={lineVariant}
                className="block font-body text-[11.5vw] font-semibold italic normal-case tracking-tight text-peach-dark sm:text-6xl lg:text-7xl"
              >
                — Closed.
              </motion.span>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base leading-relaxed text-ink-muted sm:text-lg"
          >
            You've done the hard part by showing up. The rest, we build together. I help young adults and
            professionals close the gap between who they are and who they're capable of becoming — through
            authentic career and life resets, not generic advice.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-call-btn"
              onClick={openBooking}
              className="group flex items-center gap-2 rounded-full bg-peach-dark px-7 py-4 font-heading text-sm font-bold text-cream shadow-[0_18px_45px_-14px_rgba(226,121,111,0.8)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-peach-deeper"
            >
              Book a Strategy Call — ₹199
              <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-know-more-btn"
              data-no-booking
              onClick={() => scrollToId("about")}
              className="group flex items-center gap-2 rounded-full border border-ink/15 px-7 py-4 font-heading text-sm font-bold text-ink transition-[border-color,background-color] duration-300 hover:border-peach-dark hover:bg-cream-dark"
            >
              Know More
              <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-8 font-heading text-xs font-semibold uppercase tracking-[0.2em] text-ink-subtle"
            data-testid="hero-stat-teaser"
          >
            1,000+ graduates mentored &nbsp;·&nbsp; 4,000+ students reached
          </motion.p>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div aria-hidden className="absolute -inset-5 rounded-t-full rounded-b-[2.5rem] border border-peach/40" />
            <div className="overflow-hidden rounded-t-full rounded-b-[2.5rem] shadow-[0_40px_80px_-30px_rgba(216,98,88,0.45)]">
              <motion.img
                src={IMAGES.hero}
                alt="Fouzia Tarannum — guide and mentor"
                data-testid="hero-portrait"
                style={{ y: imgY, scale: 1.15 }}
                className="h-[30rem] w-full object-cover sm:h-[34rem]"
              />
            </div>
            <button
              data-testid="hero-rotating-badge"
              onClick={openBooking}
              aria-label="Book a strategy call"
              className="absolute -bottom-8 -left-6 flex h-32 w-32 items-center justify-center sm:-left-10 sm:h-36 sm:w-36"
            >
              <svg viewBox="0 0 100 100" className="animate-spin-slow absolute inset-0 h-full w-full">
                <defs>
                  <path id="badge-circle" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
                </defs>
                <text className="fill-ink font-heading text-[8.4px] font-bold uppercase" letterSpacing="2.4">
                  <textPath href="#badge-circle">Book a strategy call • ₹199 •</textPath>
                </text>
              </svg>
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-peach-dark text-cream shadow-lg transition-transform duration-300 hover:scale-110">
                <ArrowUpRight size={22} />
              </span>
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
